<template>
  <div>
    <div
      class="dropdown"
      :class="['is-right', { 'is-active': isDropdownActive }, '!tw-z-[60]']"
    >
      <div class="dropdown-trigger">
        <Button
          :class="size"
          variant="primary"
          type="button"
          icon-before="fas fa-plus"
          icon-after="fas fa-angle-down"
          @click="toggleDropdown"
        >
          <span> New Meeting</span>
        </Button>
      </div>
      <div
        id="add-meeting-dropdown"
        class="dropdown-menu"
      >
        <div class="dropdown-content">
          <!-- Primary Action -->
          <a
            class="dropdown-item primary-action"
            @click="handleAction('join')"
          >
            <span class="icon"><i class="fas fa-robot" /></span>
            <div class="dropdown-item-content">
              <div class="dropdown-item-title">Invite Bot to Meeting (one-time)</div>
              <div class="dropdown-item-description">Add AI notetaker to your ongoing meeting</div>
            </div>
          </a>
          <hr class="dropdown-divider">
          <!-- Other Options -->
          <a
            class="dropdown-item"
            @click="handleAction('calendar')"
          >
            <span class="icon"><i class="fas fa-calendar" /></span>
            <div class="dropdown-item-content">
              <div class="dropdown-item-title">Invite Bot to Calendar Meetings (recurring)</div>
              <div class="dropdown-item-description">Have AI Notetaker automatically join your calendar meetings </div>
            </div>
          </a>

          <a
            class="dropdown-item"
            @click="handleAction('upload')"
          >
            <span class="icon"><i class="fas fa-upload" /></span>
            <div class="dropdown-item-content">
              <div class="dropdown-item-title">Upload Recording</div>
              <div class="dropdown-item-description">Upload meeting recording from your device</div>
            </div>
          </a>
          <a
            class="dropdown-item"
            @click="handleAction('record')"
          >
            <span class="icon"><i class="fas fa-video" /></span>
            <div class="dropdown-item-content">
              <div class="dropdown-item-title">Record Meeting</div>
              <div class="dropdown-item-description">Record a meeting directly within YourStake</div>
            </div>
          </a>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <CloudRecordingModal
      :is-active.sync="showCloudRecordingModal"
      class="tw-z-[60]"
      @meeting-created="handleMeetingCreated"
    />
    <div
      class="modal tw-z-[60]"
      :class="{ 'is-active': showJoinMeetingModal }"
    >
      <div
        class="modal-background"
        @click="showJoinMeetingModal = false"
      />
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">
            Join Meeting
          </p>
          <button
            class="delete"
            aria-label="close"
            @click="showJoinMeetingModal = false"
          />
        </header>
        <section class="modal-card-body">
          <InviteRecall @meeting-created="handleMeetingCreated" />
        </section>
      </div>
    </div>
    <div
      class="modal tw-z-[60]"
      :class="{ 'is-active': showMeetingCalendarSettings }"
    >
      <div
        class="modal-background"
        @click="showMeetingCalendarSettings = false"
      />
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">
            Calendar Settings
          </p>
          <button
            class="delete"
            aria-label="close"
            @click="showMeetingCalendarSettings = false"
          />
        </header>
        <section class="modal-card-body">
          <MeetingCalendarSettings />
        </section>
      </div>
    </div>
    <!--
    <ManualUploadModal
      :is-active.sync="showManualUploadModal"
      :entity-external-id="entityExternalId"
      class="tw-z-[60]"
      @meeting-created="handleMeetingCreated"
    /> -->

    <UploadRecording
      v-model="showManualUploadModal"
      :entity-external-id="entityExternalId"
      class="tw-z-[60]"
      @meeting-created="handleMeetingCreated"
    />

    <RecordModal
      :is-active.sync="showRecordModal"
      :entity-external-id="entityExternalId"
      class="tw-z-[60]"
      @meeting-created="handleMeetingCreated"
    />

    <div
      class="modal tw-z-[60]"
      :class="{ 'is-active': showNoCreditsModal }"
    >
      <div
        class="modal-background"
        @click="showNoCreditsModal = false"
      />
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">
            Not enough credits
          </p>
          <button
            class="delete"
            aria-label="close"
            @click="showNoCreditsModal = false"
          />
        </header>
        <section class="modal-card-body">
          <p v-if="canBuyCredits">
            You don't have enough credits to perform this action. Visit the Credit Dashboard to buy more credits.
          </p>
          <p v-else>
            You don't have enough credits to perform this action. Please contact your firm administrator for assistance.
          </p>
        </section>
        <footer class="modal-card-foot">
          <Button
            v-if="canBuyCredits"
            variant="primary"
            @click="goToCreditDashboard"
          >
            Go to Credit Dashboard
          </Button>
          <Button @click="showNoCreditsModal = false">Close</Button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import CloudRecordingModal from './CloudRecordingModal.vue';
import InviteRecall from './InviteRecall.vue';
import ManualUploadModal from './ManualUploadModal.vue';
import UploadRecording from '@features/meetings/components/UploadRecording.vue';
import MeetingCalendarSettings from './MeetingCalendarSettings.vue';
import RecordModal from './RecordModal.vue';
import {
  Button,
} from '@stakeui';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore';
import { mapStores } from 'pinia';
export default {
  name: 'AddMeetingButton',
  components: {
    CloudRecordingModal,
    InviteRecall,
    ManualUploadModal,
    UploadRecording,
    MeetingCalendarSettings,
    RecordModal,
    Button,
  },
  props: {
    size: {
      type: String,
      default: '',
    },
    entityExternalId: {
      type: String,
      default: '',
    },
  },
  computed: {
    ...mapStores(useAdvisorStore),
    // true if the user can purchase credits (firm admin or explicit permission)
    canBuyCredits() {
      const advisor = this.advisorStore.advisor;
      return !!(advisor && (advisor.is_firm_admin || advisor.can_purchase_credits));
    },

    // true if the action should be allowed (non-credit firms or has ≥1 credit)
    hasCredits() {
      const firm = this.advisorStore.advisor?.firm;
      if (!firm) return true;
      if (!firm.credit_based_firm) return true;
      return (firm.extraction_credits ?? 0) > 1;
    },
  },
  data() {
    return {
      isDropdownActive: false,
      showCloudRecordingModal: false,
      showJoinMeetingModal: false,
      showMeetingCalendarSettings: false,
      showManualUploadModal: false,
      showRecordModal: false,
      showNoCreditsModal: false,
    };
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    toggleDropdown() {
      this.isDropdownActive = !this.isDropdownActive;
    },
    handleAction(type) {
      if (!this.hasCredits) {
        this.showNoCreditsModal = true;
        this.isDropdownActive = false;
        return;
      }
      switch (type) {
        case 'join':
          this.showJoinMeetingModal = true;
          break;
        case 'cloud':
          this.showCloudRecordingModal = true;
          break;
        case 'calendar':
          this.showMeetingCalendarSettings = true;
          break;
        case 'upload':
          this.showManualUploadModal = true;
          break;
        case 'record':
          this.showRecordModal = true;
      }
      this.isDropdownActive = false;
    },
    goToCreditDashboard() {
      this.showNoCreditsModal = false;
      this.$router.push({ name: 'credit-dashboard' });
    },
    handleMeetingCreated() {
      console.log('MEETING CREATED');
      this.showManualUploadModal = false;
      this.showCloudRecordingModal = false;
      this.showJoinMeetingModal = false;
      this.showMeetingCalendarSettings = false;
      this.isDropdownActive = false;
      this.showRecordModal = false;
      this.$emit('meeting-created');
    },
    handleClickOutside(event) {
      if (!event.target.closest('.dropdown')) {
        this.isDropdownActive = false;
      }
    },
  },
};
</script>

<style scoped>
#add-meeting-dropdown {
  max-height: 500px !important;
}

.dropdown-menu {
  min-width: 300px;
}

.dropdown-content {
  padding: 0.5rem 0;
}

.dropdown-item {
  display: flex;
  align-items: start;
  padding: 0.75rem 1rem;
}

.dropdown-item .icon {
  margin-right: 0.75rem;
  margin-top: 0.25rem;
}

.dropdown-item-content {
  flex: 1;
}

.dropdown-item-title {
  font-weight: 500;
  margin-bottom: 0.25rem;
}

.dropdown-item-description {
  font-size: 0.875rem;
  color: #666;
}

.primary-action {
  background-color: #f0f9ff;
}

.primary-action:hover {
  background-color: #e0f2fe !important;
}

.primary-action .icon {
  color: #0284c7;
}

.primary-action .dropdown-item-title {
  color: #0284c7;
}

.dropdown-item:hover {
  background-color: #f5f5f5;
}
</style>
